import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useCommon from '@utils/hooks/common';
import { handleJoinRoom } from '@utils/index';
import { notification } from 'antd';
import { getGameEventV3 } from '@constant/game';
import { isTelegramMiniApp } from '@components/layout';

const INVITE_ROOM_STORAGE_KEY = 'pendingInviteRoomCode';
const ROOM_JOIN_LOCK_KEY = 'roomJoinInProgress';

// Global join lock to prevent multiple concurrent joins
const setJoinLock = (roomCode: string) => {
  localStorage.setItem(ROOM_JOIN_LOCK_KEY, roomCode);
};

const clearJoinLock = () => {
  localStorage.removeItem(ROOM_JOIN_LOCK_KEY);
};

const isJoinLocked = (roomCode: string): boolean => {
  const lockedRoom = localStorage.getItem(ROOM_JOIN_LOCK_KEY);
  return lockedRoom === roomCode;
};

/**
 * Hook to handle room invitations via URL query parameter or Telegram Mini App launch params
 * 
 * Flow for authenticated users:
 * 1. Detect ?inviteToRoom=XXX or tgWebAppStartParam → Join room immediately
 * 
 * Flow for non-authenticated users:
 * 1. Detect ?inviteToRoom=XXX or tgWebAppStartParam → Store in localStorage
 * 2. Show login modal
 * 3. User logs in → Auto-join room
 * 
 * Flow for new registrations:
 * 1. Detect ?inviteToRoom=XXX or tgWebAppStartParam → Store in localStorage
 * 2. Show login modal
 * 3. User registers → Complete profile (name, country)
 * 4. Complete referral code (if applicable)
 * 5. Auto-join room after profile completion
 * 
 * Telegram Mini App Support:
 * - Reads parameters from hash: #roomCode=XXX&isObserver=true
 * - Or uses tgWebAppStartParam as room code: #tgWebAppStartParam=XXX&isObserver=true
 * - Bot can pass custom params when launching: roomCode and isObserver
 */
export const useInviteToRoom = () => {
  const router = useRouter();
  const { user, system, socket, t } = useCommon({
    withTranslation: ['common', 'errorCodes'],
    observers: ['user', 'system', 'socket'],
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingRoomCode, setPendingRoomCode] = useState<string | null>(null);
  const [telegramParamsProcessed, setTelegramParamsProcessed] = useState(false);
  
  // Listen for socket authentication events
  useEffect(() => {
    const handleSocketAuthenticated = (event) => {
      console.log('[useInviteToRoom] Socket authenticated event received:', event.detail);
      
      // Check for pending room code after authentication
      const storedRoomCode = localStorage.getItem(INVITE_ROOM_STORAGE_KEY);
      if (storedRoomCode && user?.getPlayerToken() && !isProcessing) {
        console.log('[useInviteToRoom] Socket authenticated, retrying room join:', storedRoomCode);
        const isObserverMode = localStorage.getItem(INVITE_ROOM_STORAGE_KEY + '_observer') === 'true';
        
        // Directly call joinRoomWithCode after socket authentication
        setTimeout(() => {
          console.log('[useInviteToRoom] Calling joinRoomWithCode now that socket is authenticated');
          joinRoomWithCode(storedRoomCode, isObserverMode);
        }, 500);
      }
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('socketAuthenticated', handleSocketAuthenticated);
      return () => window.removeEventListener('socketAuthenticated', handleSocketAuthenticated);
    }
  }, [user, isProcessing]);
  
  // Debug log to verify hook is running
  console.log('[useInviteToRoom] Hook initialized, query:', router.query);

  // Function to join room via websocket
  const joinRoomWithCode = async (roomCode: string, isObserver: boolean = false) => {
    if (!roomCode || isProcessing) {
      console.log('[useInviteToRoom] Skipping join - no room code or already processing');
      return;
    }
    
    // Check global join lock to prevent duplicate joins
    if (isJoinLocked(roomCode)) {
      console.log('[useInviteToRoom] Room join already in progress for:', roomCode);
      return;
    }
    
    console.log('[useInviteToRoom] Starting room join process:', roomCode, 'as observer:', isObserver);
    
    // IMPORTANT: Check if socket is actually connected before attempting join
    if (!socket?.isConnected) {
      console.log('[useInviteToRoom] Socket not connected yet, storing room code and waiting...');
      // Store in localStorage and wait for socket connection
      localStorage.setItem(INVITE_ROOM_STORAGE_KEY, roomCode);
      if (isObserver) {
        localStorage.setItem(INVITE_ROOM_STORAGE_KEY + '_observer', 'true');
      }
      setPendingRoomCode(roomCode);
      return; // Don't proceed, will retry when socket connects
    }
    
    // Set global join lock BEFORE setting isProcessing
    setJoinLock(roomCode);
    setIsProcessing(true);
    
    try {
      console.log('[useInviteToRoom] Socket connected status:', socket?.isConnected);
      console.log('[useInviteToRoom] User authenticated:', !!user?.getPlayerToken());
      
      // Try direct JOIN_ROOM for invite links since REQUEST_ROOM_ACCESS might not work
      const {JOIN_ROOM} = getGameEventV3();
      
      console.log('[useInviteToRoom] Attempting direct JOIN_ROOM for invite link...');
      
      const joinRoomPayload = {
        roomCode,
        token: user?.getPlayerToken(),
        id: user?.getPlayerId(),
        isObserver: isObserver || false,
      };
      
      console.log('[useInviteToRoom] JOIN_ROOM payload:', JSON.stringify(joinRoomPayload));
      
      // Since JOIN_ROOM alone doesn't navigate properly, just use handleJoinRoom
      // which uses REQUEST_ROOM_ACCESS and handles all the navigation
      console.log('[useInviteToRoom] Using handleJoinRoom for complete flow');
      const data = {
        roomCode,
        gameType: 'FIVE_CARD_PLO',
        roomType: 'PUBLIC',
        currencyId: 'COIN',
        isAllIn: false,
        isObserver: isObserver,
      };
      const result = await handleJoinRoom(user, system, socket, data, t);
      
      // Check if the join was successful
      if (result instanceof Error) {
        console.error('[useInviteToRoom] Room join failed:', result.message);
        
        // If it's a socket authentication issue, keep the room code for retry
        if (result.message?.includes('not connected') || result.message?.includes('WS_0000')) {
          console.log('[useInviteToRoom] Socket authentication issue, keeping room code for retry');
          localStorage.setItem(INVITE_ROOM_STORAGE_KEY, roomCode);
          if (isObserver) {
            localStorage.setItem(INVITE_ROOM_STORAGE_KEY + '_observer', 'true');
          }
          setPendingRoomCode(roomCode);
        } else {
          // For other errors, clear the room code to prevent retry loops
          console.log('[useInviteToRoom] Non-recoverable error, clearing room code');
          localStorage.removeItem(INVITE_ROOM_STORAGE_KEY);
          localStorage.removeItem(INVITE_ROOM_STORAGE_KEY + '_observer');
          setPendingRoomCode(null);
        }
        
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to join room. Please try again.',
        });
      } else {
        // Success - clear the pending room code and join lock
        console.log('[useInviteToRoom] Successfully joined room:', roomCode);
        localStorage.removeItem(INVITE_ROOM_STORAGE_KEY);
        localStorage.removeItem(INVITE_ROOM_STORAGE_KEY + '_observer');
        clearJoinLock();
        setPendingRoomCode(null);
      }
    } catch (error) {
      console.error('[useInviteToRoom] Failed to join room:', error);
      
      // If it's a socket authentication issue, keep the room code for retry
      if (error?.code === 'WS_0000' || error?.message?.includes('not connected')) {
        console.log('[useInviteToRoom] Socket authentication issue, keeping room code for retry');
        localStorage.setItem(INVITE_ROOM_STORAGE_KEY, roomCode);
        if (isObserver) {
          localStorage.setItem(INVITE_ROOM_STORAGE_KEY + '_observer', 'true');
        }
        setPendingRoomCode(roomCode);
      } else {
        // For other errors, clear the room code to prevent retry loops
        localStorage.removeItem(INVITE_ROOM_STORAGE_KEY);
        localStorage.removeItem(INVITE_ROOM_STORAGE_KEY + '_observer');
        setPendingRoomCode(null);
      }
      
      notification.error({
        message: 'Error',
        description: 'Failed to join room. Please try again.',
      });
    } finally {
      // Always clear the join lock and processing flag
      clearJoinLock();
      setIsProcessing(false);
    }
  };

  // Check for inviteToRoom query parameter
  useEffect(() => {
    // Handle both correct spelling and common typo
    const { inviteToRoom, isObserver, isOberser } = router.query;
    
    // Log all query params for debugging
    console.log('[useInviteToRoom] All query params:', router.query);
    
    if (inviteToRoom && typeof inviteToRoom === 'string') {
      const roomCode = inviteToRoom;
      // Check both correct spelling and typo
      const observerMode = isObserver === 'true' || isOberser === 'true';
      
      console.log('[useInviteToRoom] Detected inviteToRoom query param:', roomCode, 'observer:', observerMode);
      
      // Always store room code in localStorage first (for persistence through login/registration)
      localStorage.setItem(INVITE_ROOM_STORAGE_KEY, roomCode);
      if (observerMode) {
        localStorage.setItem(INVITE_ROOM_STORAGE_KEY + '_observer', 'true');
      }
      setPendingRoomCode(roomCode);
      
      console.log('[useInviteToRoom] Stored room code in localStorage:', roomCode, 'as observer:', observerMode);
      
      // Check if user is authenticated
      const playerToken = user?.getPlayerToken();
      
      if (playerToken) {
        // User is authenticated, check socket connection
        console.log('[useInviteToRoom] User authenticated, socket connected:', socket?.isConnected);
        joinRoomWithCode(roomCode, observerMode);
      } else {
        // User is not authenticated
        console.log('[useInviteToRoom] User not authenticated');

        if (isTelegramMiniApp) {
          // For TG Mini App, redirect to telegram login page
          console.log('[useInviteToRoom] TG Mini App detected, redirecting to /telegram');
          router.push('/telegram');
        } else {
          // For web, show login modal
          console.log('[useInviteToRoom] Showing login modal');
          system.showModalSheet('login');

          notification.info({
            message: 'Login Required',
            description: 'Please login to join the room',
          });
        }
      }

      // Clean up the URL to remove the query parameters
      const newQuery = { ...router.query };
      delete newQuery.inviteToRoom;
      delete newQuery.isObserver;
      delete newQuery.isOberser; // Also remove typo if present
      router.replace(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query.inviteToRoom, router.query.isObserver, router.query.isOberser]);

  // Helper function to decode base64url
  const base64UrlDecode = (str: string): string => {
    try {
      // Convert base64url to base64
      let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
      // Add padding if needed
      const pad = base64.length % 4;
      if (pad) {
        if (pad === 1) {
          throw new Error('Invalid base64url string');
        }
        base64 += new Array(5 - pad).join('=');
      }
      // Decode base64
      return decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
    } catch (error) {
      console.error('[useInviteToRoom] Base64url decode error:', error);
      return str; // Return original string if decode fails
    }
  };

  // Check for Telegram Mini App launch parameters
  useEffect(() => {
    // Only process once and only if we haven't already found params from query
    if (telegramParamsProcessed || router.query.inviteToRoom) return;
    
    // Check for both tgWebAppStartParam and start_param in router.query
    const { tgWebAppStartParam, start_param } = router.query;
    let startParam = tgWebAppStartParam || start_param;
    
    // Handle malformed query params like "start_param=eyJyb29tQ29kZSI6IkdMRFpUWiJ9"
    if (!startParam) {
      const queryKeys = Object.keys(router.query);
      const malformedKey = queryKeys.find(key => 
        key.includes('start_param=') || 
        key.includes('tgWebAppStartParam=') ||
        key.includes('startParam=')
      );
      
      if (malformedKey) {
        console.log('[useInviteToRoom] Found malformed param key:', malformedKey);
        // Extract the base64 part after the = sign
        const match = malformedKey.match(/(?:start_param|tgWebAppStartParam|startParam)=(.+)/);
        if (match) {
          startParam = match[1];
          console.log('[useInviteToRoom] Extracted param value:', startParam);
        }
      }
    }
    
    if (startParam && typeof startParam === 'string') {
      console.log('[useInviteToRoom] Found start param in query (raw):', startParam);
      
      try {
        // Decode base64url and parse as JSON
        const decoded = base64UrlDecode(startParam);
        console.log('[useInviteToRoom] Decoded start param:', decoded);
        
        let roomCode = null;
        let isObserver = false;
        
        try {
          // Parse as JSON to get roomCode and isObserver
          const parsed = JSON.parse(decoded);
          console.log('[useInviteToRoom] Parsed tgWebAppStartParam as JSON:', parsed);
          
          roomCode = parsed.roomCode;
          isObserver = parsed.isObserver === true;
        } catch {
          // If not JSON, use the decoded string as room code
          console.log('[useInviteToRoom] Not JSON, using decoded string as room code');
          roomCode = decoded;
        }
        
        if (roomCode) {
          console.log('[useInviteToRoom] Telegram Mini App params - roomCode:', roomCode, 'isObserver:', isObserver);
          
          // Store in localStorage for persistence
          localStorage.setItem(INVITE_ROOM_STORAGE_KEY, roomCode);
          if (isObserver) {
            localStorage.setItem(INVITE_ROOM_STORAGE_KEY + '_observer', 'true');
          } else {
            localStorage.removeItem(INVITE_ROOM_STORAGE_KEY + '_observer');
          }
          setPendingRoomCode(roomCode);
          
          // Check if user is authenticated
          const playerToken = user?.getPlayerToken();
          
          if (playerToken && socket?.isConnected) {
            console.log('[useInviteToRoom] User authenticated and socket connected, joining via Telegram params');
            joinRoomWithCode(roomCode, isObserver);
          } else if (playerToken) {
            console.log('[useInviteToRoom] User authenticated but socket not ready, will retry when connected');
          } else {
            // User is not authenticated
            console.log('[useInviteToRoom] User not authenticated');

            if (isTelegramMiniApp) {
              // For TG Mini App, redirect to telegram login page
              console.log('[useInviteToRoom] TG Mini App detected, redirecting to /telegram');
              router.push('/telegram');
            } else {
              // For web, show login modal
              console.log('[useInviteToRoom] Showing login modal');
              system.showModalSheet('login');

              notification.info({
                message: 'Login Required',
                description: 'Please login to join the room',
              });
            }
          }
        }
      } catch (error) {
        console.error('[useInviteToRoom] Error parsing tgWebAppStartParam:', error);
      }
      
      setTelegramParamsProcessed(true);
    }
    
    // Also check if we're in Telegram Mini App environment with hash params
    if (!telegramParamsProcessed && typeof window !== 'undefined' && window.location.hash) {
      let hash = window.location.hash.slice(1); // Remove the # symbol
      
      if (hash) {
        console.log('[useInviteToRoom] Checking Telegram hash params:', hash);
        
        // Decode URL encoding if present (e.g., %3D to =)
        try {
          hash = decodeURIComponent(hash);
          console.log('[useInviteToRoom] Decoded hash:', hash);
        } catch (e) {
          console.log('[useInviteToRoom] Hash not URL encoded or decode failed');
        }
        
        try {
          const params = new URLSearchParams(hash);
          
          // Check for custom Telegram parameters
          let roomCode = params.get('roomCode');
          let isObserverParam = params.get('isObserver');
          
          // Check for tgWebAppStartParam, start_param, or startParam in hash
          const startParam = params.get('tgWebAppStartParam') || 
                            params.get('start_param') || 
                            params.get('startParam');
          
          if (startParam) {
            console.log('[useInviteToRoom] Found tgWebAppStartParam in hash (raw):', startParam);
            
            // Try to decode base64url and parse as JSON
            const decoded = base64UrlDecode(startParam);
            console.log('[useInviteToRoom] Decoded start param:', decoded);
            
            try {
              // Try parsing as JSON
              const parsed = JSON.parse(decoded);
              console.log('[useInviteToRoom] Parsed tgWebAppStartParam as JSON:', parsed);
              
              // Extract roomCode and isObserver from parsed object
              if (parsed.roomCode) {
                roomCode = roomCode || parsed.roomCode;
              }
              if (parsed.isObserver !== undefined) {
                isObserverParam = isObserverParam || String(parsed.isObserver);
              }
            } catch {
              // If not JSON, use the decoded string as room code
              if (!roomCode) {
                roomCode = decoded;
              }
            }
          }
          
          // Use roomCode from custom param or decoded startParam
          const finalRoomCode = roomCode;
          const isObserver = isObserverParam === 'true';
          
          if (finalRoomCode) {
            console.log('[useInviteToRoom] Final Telegram params - roomCode:', finalRoomCode, 'isObserver:', isObserver);
            
            // Store in localStorage for persistence
            localStorage.setItem(INVITE_ROOM_STORAGE_KEY, finalRoomCode);
            if (isObserver) {
              localStorage.setItem(INVITE_ROOM_STORAGE_KEY + '_observer', 'true');
            }
            setPendingRoomCode(finalRoomCode);
            
            // Check if user is authenticated
            const playerToken = user?.getPlayerToken();
            
            if (playerToken && socket?.isConnected) {
              console.log('[useInviteToRoom] User authenticated, attempting to join via Telegram params');
              joinRoomWithCode(finalRoomCode, isObserver);
            } else if (playerToken) {
              console.log('[useInviteToRoom] User authenticated but socket not ready, will retry when connected');
            } else {
              // User is not authenticated
              console.log('[useInviteToRoom] User not authenticated');

              if (isTelegramMiniApp) {
                // For TG Mini App, redirect to telegram login page
                console.log('[useInviteToRoom] TG Mini App detected, redirecting to /telegram');
                router.push('/telegram');
              } else {
                // For web, show login modal
                console.log('[useInviteToRoom] Showing login modal');
                system.showModalSheet('login');

                notification.info({
                  message: 'Login Required',
                  description: 'Please login to join the room',
                });
              }
            }
          }
        } catch (error) {
          console.error('[useInviteToRoom] Error parsing Telegram hash params:', error);
        }
        
        setTelegramParamsProcessed(true);
      }
    }
  }, [telegramParamsProcessed, router.query.inviteToRoom, router.query.tgWebAppStartParam, router.query.start_param, user, system, socket?.isConnected]);

  // Check for pending room code after login/registration and socket connection
  useEffect(() => {
    const playerToken = user?.getPlayerToken();
    const playerInfo = user?.playerInfo;
    const thirdPartyInfo = user?.playerThirdPartyInfo;
    const isSocketConnected = socket?.isConnected;
    
    console.log('[useInviteToRoom] Checking conditions for room join');
    console.log('[useInviteToRoom] - playerToken exists:', !!playerToken);
    console.log('[useInviteToRoom] - playerInfo:', playerInfo?.displayName);
    console.log('[useInviteToRoom] - thirdPartyInfo verified:', thirdPartyInfo?.isAccountVerified);
    console.log('[useInviteToRoom] - socket connected:', isSocketConnected);
    console.log('[useInviteToRoom] - pendingRoomCode:', pendingRoomCode);
    console.log('[useInviteToRoom] - isProcessing:', isProcessing);
    
    // Only proceed if user is authenticated
    if (!playerToken) {
      console.log('[useInviteToRoom] No player token, skipping');
      return;
    }
    
    // Check if user has completed profile setup (for new registrations)
    const isProfileComplete = playerInfo?.displayName && 
                             (thirdPartyInfo?.isAccountVerified || 
                              thirdPartyInfo?.filledReferralCode !== undefined);
    
    console.log('[useInviteToRoom] - Profile complete:', isProfileComplete);
    
    // If profile is not complete, wait for completion
    if (!isProfileComplete && thirdPartyInfo) {
      console.log('[useInviteToRoom] Profile not complete, waiting...');
      return;
    }
    
    // Check for pending room code
    if (!pendingRoomCode) {
      const storedRoomCode = localStorage.getItem(INVITE_ROOM_STORAGE_KEY);
      console.log('[useInviteToRoom] Checking localStorage for room code:', storedRoomCode);
      if (storedRoomCode) {
        setPendingRoomCode(storedRoomCode);
        return; // Will trigger re-run with pendingRoomCode set
      }
      console.log('[useInviteToRoom] No pending room code found');
      return;
    }
    
    // CRITICAL: Socket MUST be connected before attempting join
    if (!isSocketConnected) {
      console.log('[useInviteToRoom] Socket not connected, waiting for connection...');
      return;
    }
    
    // Check for enhanced authentication status if available
    const isReadyForRooms = socket?.isReadyForRoomOperations !== undefined ? 
                            socket.isReadyForRoomOperations : isSocketConnected;
    
    // Log authentication readiness
    console.log('[useInviteToRoom] Authentication status - connected:', isSocketConnected, 
                'authenticated:', socket?.isAuthenticated, 
                'ready for rooms:', isReadyForRooms);
    
    // Don't attempt if already processing
    if (isProcessing) {
      console.log('[useInviteToRoom] Already processing room join');
      return;
    }
    
    // If all conditions are met, join the room
    console.log('[useInviteToRoom] All conditions met, proceeding with room join:', pendingRoomCode);
    
    // Check if observer mode is stored
    const isObserverMode = localStorage.getItem(INVITE_ROOM_STORAGE_KEY + '_observer') === 'true';
    console.log('[useInviteToRoom] Observer mode:', isObserverMode);
    
    // Small delay to ensure all profile modals are closed and socket is ready
    setTimeout(() => {
      joinRoomWithCode(pendingRoomCode, isObserverMode);
    }, 1000);
  }, [user?.playerToken, user?.playerInfo?.displayName, user?.playerThirdPartyInfo, pendingRoomCode, socket?.isConnected, socket?.isAuthenticated, socket?.isReadyForRoomOperations, isProcessing]);

  // Monitor socket connection changes for authentication readiness
  useEffect(() => {
    if (socket?.isConnected) {
      console.log('[useInviteToRoom] Socket connected! isConnected:', socket.isConnected);
      console.log('[useInviteToRoom] Socket authentication should be ready for room joining');
      
      // Check if we have a pending room code to join after socket authentication
      const storedRoomCode = localStorage.getItem(INVITE_ROOM_STORAGE_KEY);
      if (storedRoomCode && user?.getPlayerToken()) {
        console.log('[useInviteToRoom] Found pending room code after socket connect:', storedRoomCode);
        // Trigger the main effect to re-check conditions with authenticated socket
        setPendingRoomCode(storedRoomCode);
      }
    } else {
      console.log('[useInviteToRoom] Socket disconnected, waiting for reconnection and authentication');
    }
  }, [socket?.isConnected, user]);

  // Cleanup effect to clear join lock on unmount
  useEffect(() => {
    return () => {
      // Clear join lock when component unmounts
      clearJoinLock();
    };
  }, []);

  return {
    isProcessing,
    pendingRoomCode,
    joinRoomWithCode,
  };
};